import { VITAL_LIMITS } from "./hraValidation";
import { getPhoneLimits } from "./countries";

export const REMARKS_MAX = 250;
export const BMI_LIMITS = { min: 10, max: 60 };
export const EYE_LIMITS = { min: 0.1, max: 20 };
export const NAME_MAX = 80;
export const NAME_MIN = 2;
export const MAX_AGE = 120;

export const ADDRESS_LIMITS = {
  street: 120,
  city: 50,
  state: 50,
  country: 50,
  addressType: 30,
  postalCode: 6,
};

const NAME_RE = /^[\p{L}\p{M}\s'.-]+$/u;

export function parseBp(bpString) {
  if (!bpString) return { sys: "", dia: "" };
  const m = String(bpString).match(/(\d+)\s*[/\-]\s*(\d+)/);
  if (m) return { sys: m[1], dia: m[2] };
  return { sys: "", dia: "" };
}

export function formatBp(sys, dia) {
  const s = String(sys || "").trim();
  const d = String(dia || "").trim();
  if (!s && !d) return "";
  if (s && d) return `${s}/${d}`;
  return s || d;
}

const parseEyePart = (part) => {
  const m = String(part || "").trim().match(/^(\d+\.?\d*)(?:\/(\d+\.?\d*))?$/);
  if (!m) return "";
  return m[2] || m[1];
};

export function parseEyeSight(str) {
  if (!str) return { left: "", right: "" };
  const s = String(str).trim();
  const parts = s.split(/[,;]/).map((p) => p.trim()).filter(Boolean);
  if (parts.length >= 2) {
    return { left: parseEyePart(parts[0]), right: parseEyePart(parts[1]) };
  }
  const lr = s.match(/L[:\s]*(\d+\.?\d*).*R[:\s]*(\d+\.?\d*)/i);
  if (lr) return { left: lr[1], right: lr[2] };
  const pair = s.match(/(\d+\.?\d*)\s*[/\-]\s*(\d+\.?\d*)/);
  if (pair) return { left: pair[1], right: pair[2] };
  const single = s.match(/^(\d+\.?\d*)$/);
  if (single) return { left: single[1], right: single[1] };
  return { left: "", right: "" };
}

export function formatEyeSight(left, right) {
  const l = String(left || "").trim();
  const r = String(right || "").trim();
  if (!l && !r) return "";
  if (l && r) return `${l}/${l}, ${r}/${r}`;
  if (l) return `${l}/${l}`;
  return `${r}/${r}`;
}

export function displayEyeSight(str) {
  const { left, right } = parseEyeSight(str);
  if (!left && !right) return "—";
  if (left && right) return `Left: ${left}, Right: ${right}`;
  return left ? `Left: ${left}` : `Right: ${right}`;
}

export function displayBp(str) {
  if (!str) return "—";
  return `${str} mmHg`;
}

export function clampNumericInput(value, { min, max, decimals = 1 } = {}) {
  if (value === "" || value == null) return "";
  let cleaned = String(value).replace(/[^\d.]/g, "");
  const parts = cleaned.split(".");
  if (parts.length > 2) cleaned = `${parts[0]}.${parts.slice(1).join("")}`;
  if (decimals === 0) cleaned = cleaned.replace(/\./g, "");
  const n = parseFloat(cleaned);
  if (cleaned !== "" && Number.isNaN(n)) return "";
  if (cleaned !== "" && min != null && n < min) return String(min);
  if (cleaned !== "" && max != null && n > max) return String(max);
  return cleaned;
}

export function clampIntegerInput(value, { min, max } = {}) {
  if (value === "" || value == null) return "";
  const cleaned = String(value).replace(/\D/g, "");
  if (!cleaned) return "";
  const n = parseInt(cleaned, 10);
  if (Number.isNaN(n)) return "";
  if (min != null && n < min) return String(min);
  if (max != null && n > max) return String(max);
  return cleaned;
}

export function clampRemarks(value) {
  return String(value || "").slice(0, REMARKS_MAX);
}

function validateBpFields(form, errors, prefix = "") {
  const sysKey = prefix ? `${prefix}bpSys` : "bpSys";
  const diaKey = prefix ? `${prefix}bpDia` : "bpDia";
  const sys = parseFloat(form.bpSys);
  const dia = parseFloat(form.bpDia);
  const hasSys = form.bpSys !== "" && form.bpSys != null;
  const hasDia = form.bpDia !== "" && form.bpDia != null;

  if (hasSys && (Number.isNaN(sys) || sys < VITAL_LIMITS.bpSys.min || sys > VITAL_LIMITS.bpSys.max)) {
    errors[sysKey] = `Systolic must be ${VITAL_LIMITS.bpSys.min}–${VITAL_LIMITS.bpSys.max}.`;
  }
  if (hasDia && (Number.isNaN(dia) || dia < VITAL_LIMITS.bpDia.min || dia > VITAL_LIMITS.bpDia.max)) {
    errors[diaKey] = `Diastolic must be ${VITAL_LIMITS.bpDia.min}–${VITAL_LIMITS.bpDia.max}.`;
  }
  if (hasSys && hasDia && !errors[sysKey] && !errors[diaKey] && dia >= sys) {
    errors.bp = "Diastolic must be lower than systolic.";
  }
}

function validateEyeFields(form, errors) {
  ["eyeLeft", "eyeRight"].forEach((key) => {
    const v = form[key];
    if (v === "" || v == null) return;
    const n = parseFloat(v);
    if (Number.isNaN(n) || n < EYE_LIMITS.min || n > EYE_LIMITS.max) {
      errors[key] = `Must be ${EYE_LIMITS.min}–${EYE_LIMITS.max}.`;
    }
  });
}

function validateNumericOptional(value, key, errors, { min, max, label }) {
  if (value === "" || value == null) return;
  const n = parseFloat(value);
  if (Number.isNaN(n) || n < min || n > max) {
    errors[key] = `${label} must be ${min}–${max}.`;
  }
}

export function validateHealthDetails(form) {
  const errors = {};

  validateNumericOptional(form.height, "height", errors, {
    min: VITAL_LIMITS.heightCm.min,
    max: VITAL_LIMITS.heightCm.max,
    label: "Height",
  });
  validateNumericOptional(form.weight, "weight", errors, {
    min: VITAL_LIMITS.weightKg.min,
    max: VITAL_LIMITS.weightKg.max,
    label: "Weight",
  });
  validateNumericOptional(form.BMI, "BMI", errors, {
    min: BMI_LIMITS.min,
    max: BMI_LIMITS.max,
    label: "BMI",
  });
  validateBpFields(form, errors);
  validateEyeFields(form, errors);

  if (form.eyeCheckupResults && form.eyeCheckupResults.length > REMARKS_MAX) {
    errors.eyeCheckupResults = `Maximum ${REMARKS_MAX} characters.`;
  }

  return { valid: Object.keys(errors).length === 0, errors };
}

export function validateFamilyMember(form, dialCode) {
  const errors = {};
  const name = (form.fullName || "").trim();

  if (!name) errors.fullName = "Full name is required.";
  else if (name.length < NAME_MIN || name.length > NAME_MAX) {
    errors.fullName = `Name must be ${NAME_MIN}–${NAME_MAX} characters.`;
  } else if (!NAME_RE.test(name)) {
    errors.fullName = "Name contains invalid characters.";
  }

  if (!form.relation) errors.relation = "Relation is required.";

  if (form.DOB) {
    const birth = new Date(`${form.DOB}T00:00:00`);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    if (Number.isNaN(birth.getTime()) || birth >= today) {
      errors.DOB = "Date of birth must be before today.";
    }
  }

  if (form.age !== "" && form.age != null) {
    const age = parseInt(form.age, 10);
    if (Number.isNaN(age) || age < 0 || age > MAX_AGE) {
      errors.age = `Age must be 0–${MAX_AGE}.`;
    }
  }

  const phoneDigits = String(form.phoneRaw || form.mobileNumber || "").replace(/\D/g, "");
  if (phoneDigits) {
    const { min, max } = getPhoneLimits(dialCode || "+91");
    if (phoneDigits.length < min || phoneDigits.length > max) {
      errors.phoneRaw = `Enter ${min}${min !== max ? `–${max}` : ""} digits for this country.`;
    }
  }

  validateNumericOptional(form.height, "height", errors, {
    min: VITAL_LIMITS.heightCm.min,
    max: VITAL_LIMITS.heightCm.max,
    label: "Height",
  });
  validateNumericOptional(form.weight, "weight", errors, {
    min: VITAL_LIMITS.weightKg.min,
    max: VITAL_LIMITS.weightKg.max,
    label: "Weight",
  });
  validateNumericOptional(form.BMI, "BMI", errors, {
    min: BMI_LIMITS.min,
    max: BMI_LIMITS.max,
    label: "BMI",
  });
  validateBpFields(form, errors);
  validateEyeFields(form, errors);

  if (form.description && form.description.length > REMARKS_MAX) {
    errors.description = `Maximum ${REMARKS_MAX} characters.`;
  }

  return { valid: Object.keys(errors).length === 0, errors };
}

export function validateAddress(form) {
  const errors = {};

  if (!(form.street || "").trim()) errors.street = "Street is required.";
  else if (form.street.length > ADDRESS_LIMITS.street) {
    errors.street = `Maximum ${ADDRESS_LIMITS.street} characters.`;
  }

  if (!(form.city || "").trim()) errors.city = "City is required.";
  else if (form.city.length > ADDRESS_LIMITS.city) {
    errors.city = `Maximum ${ADDRESS_LIMITS.city} characters.`;
  }

  if (!(form.state || "").trim()) errors.state = "State is required.";
  else if (form.state.length > ADDRESS_LIMITS.state) {
    errors.state = `Maximum ${ADDRESS_LIMITS.state} characters.`;
  }

  if (!(form.country || "").trim()) errors.country = "Country is required.";
  else if (form.country.length > ADDRESS_LIMITS.country) {
    errors.country = `Maximum ${ADDRESS_LIMITS.country} characters.`;
  }

  const pin = String(form.postalCode || "").replace(/\D/g, "");
  if (!pin) errors.postalCode = "Postal code is required.";
  else if (pin.length !== ADDRESS_LIMITS.postalCode) {
    errors.postalCode = `Postal code must be exactly ${ADDRESS_LIMITS.postalCode} digits.`;
  }

  if (form.addressType && form.addressType.length > ADDRESS_LIMITS.addressType) {
    errors.addressType = `Maximum ${ADDRESS_LIMITS.addressType} characters.`;
  }

  return { valid: Object.keys(errors).length === 0, errors };
}

// ponytail: self-check — fails if parsers drift
if (typeof process !== "undefined" && process.env?.NODE_ENV !== "production") {
  const bp = parseBp("120/80");
  console.assert(bp.sys === "120" && bp.dia === "80");
  console.assert(formatBp("120", "80") === "120/80");
  const eye = parseEyeSight("6/6, 6/9");
  console.assert(eye.left === "6" && eye.right === "9");
  console.assert(validateAddress({ postalCode: "12345" }).valid === false);
}
